import React from 'react';
import { connect } from 'react-redux';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import { formatAmount, formatDate, formatSignedAmount } from '../../utils';
import { PermissionContext } from '../../contexts';
import { showError } from '../../actions/notificationActions';
import Loading from '../Loading';
import PaymentNoteHistory from './PaymentNoteHistory';
import PaymentNoteModal from './PaymentNoteModal';
import SetOverdraftLimitModal from './SetOverdraftLimitModal';
import {
  FACILITY_STATE,
  facilityState,
  getWallet,
  getOverdraftHistory,
} from '../../services/overdraft.service';
import DataTable from '../DataTable';

const HISTORY_LIMIT = 30;

const STATE_BADGE = {
  [FACILITY_STATE.NONE]: { color: 'secondary', label: 'No facility' },
  [FACILITY_STATE.IN_CREDIT]: { color: 'success', label: 'In credit' },
  [FACILITY_STATE.DRAWING]: { color: 'warning', label: 'Drawing on overdraft' },
  [FACILITY_STATE.OVER_LIMIT]: { color: 'danger', label: 'Over limit' },
};

// The whole panel is fed by one request: `GET …/wallet` returns the stored
// columns in `data` and every derived overdraft figure in `meta`. There is no
// `…/wallet/overdraft` GET and no `…/wallet/summary` to call alongside it.
//
// Reading is not ACL-gated. Changing the limit and issuing a payment note both
// need `user-wallet:update` (FINANCE and SUPER_ADMIN), so those affordances are
// hidden up front — a denial is a clean 403 now, but there is no reason to show
// an operator a button that will only ever refuse them.
const Stat = ({ label, value, className }) => (
  <Col md={3} xs={6} className="mb-2">
    <small className="text-muted d-block">{label}</small>
    <h5 className={`mb-0 ${className || ''}`}>{value}</h5>
  </Col>
);

class UserOverdraft extends React.Component {
  state = {
    loading: false,
    error: null,
    wallet: null,

    historyOpen: false,
    historyLoaded: false,
    historyLoading: false,
    historyError: null,
    historyRows: [],
    historyMeta: { total: 0, pages: 1 },
    historyPage: 1,

    setLimitOpen: false,
    paymentNoteOpen: false,

    // Bumped when something changes the live overdraft debt, so the payment
    // note table re-reads its `meta.outstanding_now`.
    notesToken: 0,
  };

  componentDidMount() {
    this.loadWallet();
  }

  // A wallet credit or debit moves the balance, and the debt IS the negative
  // balance — so a write on the tab above changes every figure in this panel.
  // The limit is untouched by it, so the limit history is left alone.
  componentDidUpdate(prevProps) {
    if (prevProps.refreshToken !== this.props.refreshToken) {
      this.loadWallet();
      this.bumpNotes();
    }
  }

  userId = () => this.props.user.id;

  bumpNotes = () =>
    this.setState((state) => ({ notesToken: state.notesToken + 1 }));

  loadWallet = () => {
    this.setState({ loading: true, error: null });

    getWallet(this.userId())
      .then((wallet) => {
        this.setState({ loading: false, wallet });
      })
      .catch((err) => {
        this.setState({
          loading: false,
          error: (err && err.message) || 'Unable to load the wallet.',
        });
        this.props.dispatch(showError(err));
      });
  };

  loadHistory = () => {
    this.setState({ historyLoading: true, historyError: null });

    getOverdraftHistory(this.userId(), {
      page: this.state.historyPage,
      limit: HISTORY_LIMIT,
    })
      .then(({ rows, meta }) => {
        this.setState({
          historyLoading: false,
          historyLoaded: true,
          historyRows: rows,
          historyMeta: meta,
        });
      })
      .catch((err) => {
        this.setState({
          historyLoading: false,
          historyLoaded: true,
          historyError:
            (err && err.message) || 'Unable to load the limit history.',
        });
      });
  };

  setHistoryPage = (page) => {
    if (!page) return;
    this.setState({ historyPage: page }, this.loadHistory);
  };

  openSetLimit = () => this.setState({ setLimitOpen: true });

  closeSetLimit = () => this.setState({ setLimitOpen: false });

  openPaymentNote = () => this.setState({ paymentNoteOpen: true });

  closePaymentNote = () => this.setState({ paymentNoteOpen: false });

  openHistory = () => {
    this.setState({ historyOpen: true }, () => {
      const { historyLoaded, historyLoading } = this.state;
      if (!historyLoaded && !historyLoading) this.loadHistory();
    });
  };

  closeHistory = () => this.setState({ historyOpen: false });

  // The PATCH response mirrors `GET …/wallet`, so a successful limit change
  // hands us the new state directly and costs no extra request. A write parked
  // for approval never gets here (it rejects with a 403), so there is nothing
  // optimistic about applying it.
  //
  // The limit history is only refetched if it is on screen; otherwise it is
  // invalidated so the next open pulls a fresh page one.
  refresh = (wallet) => {
    if (wallet) this.setState({ wallet, loading: false, error: null });
    else this.loadWallet();

    this.setState({ historyPage: 1, historyLoaded: false }, () => {
      if (this.state.historyOpen) this.loadHistory();
    });
  };

  // Issuing a note does not move money, but it does add a row and the table's
  // headline figure comes off the live wallet, so both re-read.
  onNoteIssued = () => {
    this.bumpNotes();
    this.loadWallet();
  };

  renderPanelBody() {
    const { loading, error, wallet } = this.state;

    if (loading) return <Loading />;

    if (error) {
      return (
        <div className="text-center" style={{ padding: '20px 0' }}>
          <p className="text-muted">{error}</p>
          <Button color="primary" size="sm" onClick={this.loadWallet}>
            Retry
          </Button>
        </div>
      );
    }

    if (!wallet) return null;

    const state = facilityState(wallet);

    return (
      <React.Fragment>
        <Row>
          {/* The balance is negative exactly while the customer is in debt. */}
          <Stat
            label="Balance"
            value={formatSignedAmount(wallet.balance)}
            className={wallet.balance < 0 ? 'text-danger' : ''}
          />
          <Stat label="Overdraft limit" value={formatAmount(wallet.limit)} />
          {wallet.active ? (
            <Stat
              label="Currently owed"
              value={formatAmount(wallet.used)}
              className="text-danger"
            />
          ) : null}
          <Stat
            label="Available to spend"
            value={formatSignedAmount(wallet.spendingPower)}
            className={wallet.spendingPower <= 0 ? 'text-danger' : ''}
          />
        </Row>

        {state === FACILITY_STATE.NONE ? (
          <p className="text-muted mb-0">
            No overdraft facility. This customer cannot spend past a zero
            balance.
          </p>
        ) : null}

        {state === FACILITY_STATE.IN_CREDIT ? (
          <p className="text-muted mb-0">
            In credit. {formatAmount(wallet.available)} of the facility is
            available if the balance runs out.
          </p>
        ) : null}

        {state === FACILITY_STATE.DRAWING ? (
          <Alert color="warning" className="mb-0">
            Drawing on overdraft — owes{' '}
            <strong>{formatAmount(wallet.used)}</strong> of a{' '}
            {formatAmount(wallet.limit)} facility, with{' '}
            {formatAmount(wallet.available)} still available. The debt is repaid
            automatically the next time the wallet is credited.
          </Alert>
        ) : null}

        {state === FACILITY_STATE.OVER_LIMIT ? (
          <Alert color="danger" className="mb-0">
            Over limit — owes <strong>{formatAmount(wallet.used)}</strong>{' '}
            against a limit of {formatAmount(wallet.limit)}.{' '}
            <strong>Further spending is blocked</strong> until the debt is
            repaid. This happens when the limit is reduced below what is already
            drawn; it is not a data error.
          </Alert>
        ) : null}

        {wallet.updatedAt ? (
          <small className="text-muted d-block mt-2">
            Wallet last changed {formatDate(wallet.updatedAt)}
          </small>
        ) : null}
      </React.Fragment>
    );
  }

  renderHistoryBody() {
    const {
      historyLoading,
      historyLoaded,
      historyError,
      historyRows,
      historyMeta,
      historyPage,
    } = this.state;

    if (historyError) {
      return (
        <div className="text-center" style={{ padding: '20px 0' }}>
          <p className="text-muted">{historyError}</p>
          <Button color="primary" size="sm" onClick={this.loadHistory}>
            Retry
          </Button>
        </div>
      );
    }

    // `!historyLoaded` also covers the first render after the modal opens but
    // before the fetch has started, so the empty state never flashes.
    if (historyLoading || !historyLoaded) return <Loading />;

    if (!historyRows.length) {
      return (
        <div className="text-center text-muted" style={{ padding: '20px 0' }}>
          This wallet’s overdraft limit has never been changed.
        </div>
      );
    }

    return (
      <DataTable
        card={false}
        minWidth={800}
        page={historyPage}
        total={historyMeta.pages || 1}
        onPageChange={this.setHistoryPage}
        showPagination={historyRows.length > 0}
      >
        <thead className="text-primary">
          <tr>
            <th>Date</th>
            <th>Limit</th>
            <th>Balance at change</th>
            <th>Reason</th>
            <th>Performed by</th>
          </tr>
        </thead>
        <tbody>
          {historyRows.map((h) => (
            <tr key={h.id}>
              <td>{h.created_at ? formatDate(h.created_at) : '—'}</td>
              <td>
                {formatAmount(h.previous_limit)}
                {' → '}
                <strong>{formatAmount(h.new_limit)}</strong>
              </td>
              <td
                className={Number(h.balance_at_change) < 0 ? 'text-danger' : ''}
              >
                {formatSignedAmount(h.balance_at_change)}
              </td>
              <td>{h.reason || <span className="text-muted">—</span>}</td>
              {/* There is no admin-name resolver on this page and no lookup
                  endpoint to add, so the raw admin user id is shown. */}
              <td>{h.performed_by ? `#${h.performed_by}` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    );
  }

  renderHistoryModal() {
    const { historyOpen, historyMeta } = this.state;

    return (
      <Modal size="lg" isOpen={historyOpen} toggle={this.closeHistory}>
        <ModalHeader toggle={this.closeHistory}>
          Overdraft limit history
          {historyMeta.total ? (
            <span className="text-muted">
              {' '}
              — {historyMeta.total} change
              {historyMeta.total === 1 ? '' : 's'}
            </span>
          ) : null}
        </ModalHeader>
        <ModalBody>{this.renderHistoryBody()}</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.closeHistory}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  // `canUpdate` is resolved here, in the main React tree, and passed down —
  // it is deliberately NOT read from inside a modal.
  //
  // reactstrap 5.0.0-alpha.4 mounts a Modal with
  // `ReactDOM.unstable_renderSubtreeIntoContainer`, which starts a SEPARATE
  // React root rather than a portal. Legacy context crosses that boundary; the
  // `createContext` API does not, so a `PermissionContext.Consumer` rendered
  // inside a Modal receives the context's default value — `null` — and blows up
  // on destructuring. Every gated modal in this app is gated by its caller for
  // this reason.
  renderContent(hasPermission) {
    const { wallet, setLimitOpen, paymentNoteOpen, notesToken } = this.state;

    const badge = STATE_BADGE[facilityState(wallet)];
    const canUpdate = hasPermission('user-wallet', 'update');

    return (
      <React.Fragment>
        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {/* This card carries the wallet balance as well as the facility
                    it depends on — the tab no longer shows it separately. */}
                <strong>Wallet Summary</strong>{' '}
                <Badge color={badge.color}>{badge.label}</Badge>
              </div>
              <div>
                {/* Previewing a payment note has no side effects, so the dialog
                    itself is open to everyone; only the issue action inside it
                    is gated. */}
                <Button
                  outline
                  color="secondary"
                  size="sm"
                  className="mr-2"
                  onClick={this.openPaymentNote}
                  disabled={this.state.loading || !wallet}
                >
                  Generate payment note
                </Button>
                <Button
                  outline
                  color="secondary"
                  size="sm"
                  className="mr-2"
                  onClick={this.openHistory}
                >
                  Limit history
                </Button>
                {canUpdate ? (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={this.openSetLimit}
                    disabled={this.state.loading || !wallet}
                  >
                    Set limit
                  </Button>
                ) : null}
              </div>
            </div>
          </CardHeader>
          <CardBody>{this.renderPanelBody()}</CardBody>
        </Card>

        <PaymentNoteHistory
          userId={this.props.user.id}
          refreshToken={notesToken}
        />

        {this.renderHistoryModal()}

        <SetOverdraftLimitModal
          isOpen={setLimitOpen}
          userId={this.props.user.id}
          wallet={wallet}
          onClose={this.closeSetLimit}
          onSuccess={this.refresh}
        />

        <PaymentNoteModal
          isOpen={paymentNoteOpen}
          userId={this.props.user.id}
          canIssue={canUpdate}
          onClose={this.closePaymentNote}
          onIssued={this.onNoteIssued}
        />
      </React.Fragment>
    );
  }

  render() {
    return (
      <PermissionContext.Consumer>
        {/* Defensive: outside a PermissionProvider the context value is null.
            Deny rather than crash — the server enforces the grant regardless. */}
        {(ctx) =>
          this.renderContent(
            ctx && ctx.hasPermission ? ctx.hasPermission : () => false
          )
        }
      </PermissionContext.Consumer>
    );
  }
}

export default connect()(UserOverdraft);
